import { RefinedDataReturn } from "@/lib/useQuery/useGetExportData";
import { AddCrmModalStep, CrmBranding, CrmExportHandlerProps, CrmFieldTypeMap, CrmSystemFieldType } from ".";
import { crmSystem } from "@/constants/crm/_shared/systems";
import { NotFoundRenderer } from "../../crm/_common/FieldRenderer";
import { NotFoundForm } from "../../crm/_common/FieldForm";
import { Autocomplete } from "@/types/utils";
import { ConfigurationParametersInputProps } from "../../crm/_common/ConfigurationParameterInput";
import { createFieldParser, ParserMap } from "../../crm/_common/useGetExportData/parser/_core";
import { UseFormReturn } from "react-hook-form";
import { QuestionFormSchemaType } from "@/features/question/components/organisms/QuestionForm";
import { merge } from "lodash";

export abstract class CrmSystem<thisCrmSystem extends crmSystem> {
    abstract id: thisCrmSystem;
    abstract name: string;
    abstract fieldTypes: Record<CrmFieldTypeMap[thisCrmSystem], CrmSystemFieldType>;
    abstract exportHandler: (props: CrmExportHandlerProps) => void;
    abstract branding: CrmBranding;
    notSupportedFieldTypes?: string[]
    public isFieldTypeSupported(fieldType: string) {
        return Object.keys(this.fieldTypes).includes(fieldType)
    }
    abstract addCrmModalLimitedSteps?: Array<AddCrmModalStep>;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    resetForm(field: CrmFieldTypeMap[thisCrmSystem], form: UseFormReturn<QuestionFormSchemaType>): false | void {
        return false;
    }
    configurationParameterInput?: React.FC<ConfigurationParametersInputProps>
    public getFieldFormField(fieldType: Autocomplete<CrmFieldTypeMap[thisCrmSystem]>) {
        const field = this.fieldTypes[fieldType as CrmFieldTypeMap[thisCrmSystem]];
        if (!field) {
            return NotFoundForm;
        }
        return field.formComponent;
    }
    public getFieldRendererField(fieldType: Autocomplete<CrmFieldTypeMap[thisCrmSystem]>) {
        const field = this.fieldTypes[fieldType as CrmFieldTypeMap[thisCrmSystem]];
        if (!field) {
            return NotFoundRenderer;
        }
        return field.rendererComponent;
    }
    public getFieldFormFieldsList(): Array<string> {
        return Object.values(this.fieldTypes).map(field => (field as CrmSystemFieldType).name);
    }
    public async parser(data: RefinedDataReturn): Promise<RefinedDataReturn> {
        if (!data || data.refinedData.length === 0) return data;
        const parserMap: ParserMap = Object.entries(this.fieldTypes).reduce((acc, [key, value]) => {
            acc[key] = (value as CrmSystemFieldType).parser;
            return acc;
        }, {} as ParserMap);
        if (!parserMap || Object.keys(parserMap).length === 0) return data;
        const parseField = createFieldParser(parserMap);
        return {
            ...data,
            refinedData: data.refinedData.map(item => parseField(item))
        };
    }

    public getFieldsList() {
        return Object.keys(this.fieldTypes).map(key => {
            const field = this.fieldTypes[key as CrmFieldTypeMap[thisCrmSystem]];
            return {
                id: field.id,
                name: field.name,
            };
        });
    }

    public getFieldById(fieldId: string) {
        if (!Object.values<CrmSystemFieldType>(this.fieldTypes).find(field => field.id === fieldId)) throw Error("Provided field doesn't exist for this CRM System");
        return Object.values<CrmSystemFieldType>(this.fieldTypes).find(field => field.id === fieldId);
    }

    public getBranding(): CrmBranding {
        const fallbackBranding: CrmBranding = {
            logoUrl: "/fallback-logo.png",
            color: "#fff",
        }
        return merge(fallbackBranding, this.branding);
    }
}